import { useState } from "react";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { api, qs } from "@/lib/api";
import { BRANDS, brandById } from "@/lib/brands";
import type { Contact } from "@/lib/types";
import { BrandMark } from "@/components/BrandMark";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { SUPPRESSION_REASON } from "@/features/email/SuppressionsPage";

/** One email_suppressions row (crm-email's suppressions_list). */
export interface AddressBlock {
  id: number;
  email: string;
  /** null = every brand */
  brand_id: number | null;
  reason: string;
  source: string;
  created_at: string;
}

/** Opt-outs are per address (Brandon, 2026-10-30): each of the contact's addresses is looked up on its own, by exact
 *  address, so blocks recorded without this contact's ID (bounces, imports) show too. */
export function useAddressBlocks(emails: string[]) {
  const results = useQueries({
    queries: emails.map((email) => ({
      queryKey: ["address-blocks", email.toLowerCase()],
      queryFn: () => api<{ suppressions: AddressBlock[] }>("/api/v1/suppressions" + qs({ email, limit: 50 })).then((r) => r.suppressions),
      staleTime: 30_000,
      retry: false,
    })),
  });
  const byEmail = new Map<string, AddressBlock[]>();
  emails.forEach((e, i) => byEmail.set(e.toLowerCase(), results[i]?.data ?? []));
  return (email: string) => byEmail.get(email.toLowerCase()) ?? [];
}

const reasonText = (r: string) => (SUPPRESSION_REASON[r]?.label ?? r.replace(/_/g, " ")).toLowerCase();

/** What stops marketing at one address: blocks on every brand first, then each brand's (an unsubscribe there sends
 *  that brand's email to the person's next address). Nothing when the address is clear. */
export function AddressBlocks({ blocks }: { blocks: AddressBlock[] }) {
  if (blocks.length === 0) return null;
  const all = blocks.filter((b) => b.brand_id == null);
  const perBrand = blocks.filter((b) => b.brand_id != null).sort((a, b) => (a.brand_id ?? 0) - (b.brand_id ?? 0));
  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 pl-5 text-xs text-[var(--color-muted-foreground)]">
      {all.map((b) => (
        <Badge key={b.id} variant={SUPPRESSION_REASON[b.reason]?.tone ?? "muted"}>
          all brands: {reasonText(b.reason)}
        </Badge>
      ))}
      {perBrand.map((b) => (
        <span key={b.id} className="inline-flex items-center gap-1" title={`${brandById(b.brand_id)?.name ?? b.brand_id}: ${SUPPRESSION_REASON[b.reason]?.label ?? b.reason}`}>
          <BrandMark id={b.brand_id} dim />
          {reasonText(b.reason)}
        </span>
      ))}
    </div>
  );
}

/** email_subscription_set: a person's own request to stop or restart one brand's marketing email, at one address or
 *  at every address on file. Staff wording only. */
export function MarketingDialog({ c, initialEmail, onClose }: { c: Contact; initialEmail?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [brandId, setBrandId] = useState<number>(c.brands[0]?.brand_id ?? 2);
  const [status, setStatus] = useState<"unsubscribed" | "subscribed">("unsubscribed");
  const [email, setEmail] = useState(initialEmail ?? "");
  const brand = brandById(brandId);
  const save = useMutation({
    // Resubscribing lifts the person's own unsubscribe: the server asks for confirm, given by choosing "They asked to: Get it again".
    mutationFn: () =>
      api(`/api/v1/contacts/${c.id}/email-subscription`, { json: { brand_id: brandId, status, email: email || undefined, confirm: status === "subscribed" || undefined } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["contact", c.id] });
      void qc.invalidateQueries({ queryKey: ["address-blocks"] });
      void qc.invalidateQueries({ queryKey: ["timeline", c.id] });
      toast(`${status === "unsubscribed" ? "Unsubscribed" : "Resubscribed"} ${email || "every address"} ${status === "unsubscribed" ? "from" : "to"} ${brand?.short ?? "the brand"}`);
      onClose();
    },
    onError: (e) => toast(e.message, "error"),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(480px,calc(100vw-2rem))]">
        <DialogTitle>Marketing email</DialogTitle>
        <DialogDescription>
          Record what the person asked for. Unsubscribing one address sends that brand's email to their next address. Resubscribing never lifts a bounce, a spam
          complaint or an all-brands block.
        </DialogDescription>
        <div className="grid gap-3 text-sm sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="mk-brand">Brand</Label>
            <Select id="mk-brand" value={brandId} onChange={(e) => setBrandId(Number(e.target.value))}>
              {BRANDS.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="mk-status">They asked to</Label>
            <Select id="mk-status" value={status} onChange={(e) => setStatus(e.target.value as "unsubscribed" | "subscribed")}>
              <option value="unsubscribed">Stop this brand's email</option>
              <option value="subscribed">Get it again</option>
            </Select>
          </div>
          {c.emails.length > 1 && (
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="mk-email">Address</Label>
              <Select id="mk-email" value={email} onChange={(e) => setEmail(e.target.value)}>
                <option value="">All addresses</option>
                {c.emails.map((e) => (
                  <option key={e.id} value={e.email}>
                    {e.email}
                  </option>
                ))}
              </Select>
            </div>
          )}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={save.isPending} onClick={() => save.mutate()}>
            {status === "unsubscribed" ? "Unsubscribe" : "Resubscribe"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
